import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLocalizations } from '@/hooks/useLocalizations';

export type SenderStatus = 'seeking_more' | 'in_progress' | 'resolved';

export interface Opportunity {
  id: string;
  recipient_id: string;
  sender_email: string;
  sender_name: string | null;
  subject: string;
  message: string;
  service_type: string | null;
  location: string | null;
  budget_range: string | null;
  timeline: string | null;
  status: string;
  user_status: SenderStatus;
  created_at: string;
  updated_at: string;
}

export function useUserOpportunities() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { lCommon } = useLocalizations();

  // Fetch all opportunities sent by the current user
  const { data: opportunities, isLoading } = useQuery({
    queryKey: ['user-opportunities', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      
      const data = await db.fetchAll<Opportunity>('opportunities', '*', {
        filters: [{ column: 'sender_email', operator: 'eq', value: user.email }],
        orderBy: { column: 'created_at', ascending: false }
      });

      return data;
    },
    enabled: !!user?.email,
  });

  // Fetch active opportunities (seeking_more status for showing suggestions)
  const { data: activeOpportunities } = useQuery({
    queryKey: ['active-opportunities', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      
      const data = await db.fetchAll<Opportunity>('opportunities', '*', {
        filters: [
          { column: 'sender_email', operator: 'eq', value: user.email },
          { column: 'user_status', operator: 'eq', value: 'seeking_more' }
        ],
        orderBy: { column: 'created_at', ascending: false }
      });

      return data;
    },
    enabled: !!user?.email,
  });

  // Update sender status for similar opportunities
  const updateSenderStatus = useMutation({
    mutationFn: async ({ subject, senderStatus }: { subject: string; senderStatus: SenderStatus }) => {
      if (!user?.email) throw new Error('User not authenticated');

      // Update all opportunities with the same subject using db.raw for multiple .eq() chaining
      const { data, error } = await db.raw
        .from('opportunities')
        .update({ user_status: senderStatus })
        .eq('sender_email', user.email)
        .eq('subject', subject)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['active-opportunities'] });
      
      const statusMessageKeys: Record<SenderStatus, string> = {
        seeking_more: 'toast.marked_seeking_more.success',
        in_progress: 'toast.marked_in_progress.success',
        resolved: 'toast.marked_resolved.success',
      };
      const statusDefaults: Record<SenderStatus, string> = {
        seeking_more: 'Marked as seeking more experts',
        in_progress: 'Marked as in progress',
        resolved: 'Marked as resolved',
      };
      
      toast.success(lCommon(statusMessageKeys[variables.senderStatus], { 
        defaultValue: statusDefaults[variables.senderStatus] 
      }));
    },
    onError: () => {
      toast.error(lCommon('toast.failed_to_update_status.error', { defaultValue: 'Failed to update status' }));
    },
  });

  return {
    opportunities,
    activeOpportunities,
    isLoading,
    updateSenderStatus,
  };
}
